import {
    DisplayFeatureType, Ground3dData, NvdbFeature, SceneData,
    UserSession, DisplayFeature, ValueType, CopiableGeometryFeature, GeometryType,
    SpecialAttribute
} from './index';
import datacatalog, {AttributeType} from '../../../../integration/datacatalog/datacatalog-services';
import {FeatureTypeSummary} from '../../../../integration/representation/feature-type';
import {RoadSegment} from '../../../../integration/representation/road-segment';
import {OrderedComments} from '../selectors';

export type splitDirection = 'horizontal' | 'vertical';
export class GridCoordinates {
    constructor(
        readonly x: number,
        readonly y: number
    ) {}
    public isEqualTo(coordinates: GridCoordinates): boolean {
        return coordinates.y === this.y && coordinates.x === this.x;
    }
}

export class PreserveFocusData {
    constructor(
        readonly featureId: string,
        readonly waitUntilLoaded: boolean
    ) {}
}


export class SortSetting {
    constructor(
        readonly sortBy: SortBy,
        readonly attribute: AttributeType,
        readonly qualityParam: QualityParam,
        readonly sortOrder: SortOrder
    ) {}
}

export class QualityParam {
    constructor (
        readonly key: string,
        readonly name: string,
        readonly type: ValueType
    ) {}
}


export enum SortOrder {
    ASC = 1,
    DESC = -1
}

export enum SortBy {
    ATTRIBUTE = 1,
    NAME,
    OPERATION,
    ROAD,
    SIDE_POSITION,
    LANE,
    QUALITY_PARAMETER
}

export enum PopupType {
    NONE,
    COPY_FEATURE_WITH_TYPE
}

export enum CreatingObjectGeometryType {
    NONE,
    DRAW_POINT,
    DRAW_LINE,
    DRAW_POLYGON,
    SELECT
}

export default class FeaturesState {
    constructor(
        readonly contractId: string = null,
        readonly isFetching: boolean = false,
        readonly featureTypes: FeatureTypeSummary[] = [],
        readonly selectedFeatureTypeId: number = null,
        readonly selectedFeatureType: DisplayFeatureType = null,
        readonly additionalSelectedFeatureTypes: DisplayFeatureType[] = [],
        readonly features: DisplayFeature[] = [],
        readonly selectedFeatures: string[] = [],
        readonly user: UserSession = null,
        readonly isShowingExistingFeatures: boolean = false,
        readonly existingFeatures: NvdbFeature[] = [],
        readonly closings: DisplayFeature[] = [],
        readonly showingFullscreen: boolean = false,
        readonly splitScreenDirection: splitDirection = 'horizontal',
        readonly editingGeometryFeatureId: string = null,
        readonly sceneData: SceneData = null,
        readonly ground3dData: Ground3dData = null,
        readonly datacatalog: datacatalog = null,
        readonly gridFocus: GridCoordinates = new GridCoordinates(0, 0),
        readonly showEditor: boolean = false,
        readonly sortSetting: SortSetting = null,
        readonly showSubmittedFeatures: boolean = true,
        readonly preserveFocus: PreserveFocusData = null,
        readonly showOnlyObjektlistAttributes = true,
        readonly showOnlySelectedFeatures = false,
        readonly showQualityParams = false,
        readonly useDescriptiveName = false,
        readonly popup = PopupType.NONE,
        readonly selectedNvdbRoadObject: NvdbFeature = null,
        readonly missingAttributeTypes: AttributeType[] = [],
        readonly fetchingMissingAttributeTypes: boolean = false,
        readonly fetchingSensitiveAttributeTypes: boolean = false,
        readonly sensitiveAttributesInURL: boolean = false,
        readonly copyGeometryModalVisible: boolean = false,
        readonly copiableGeometries: CopiableGeometryFeature[] = [],
        readonly fetchingCopiableGeometries: boolean = false,
        readonly copyingGeometriesBetweenFeatures: boolean = false,
        readonly roadSystemReferences: Record<string, string> = {},
        readonly comments: OrderedComments = {},
        readonly commentsModalVisible: boolean = false,
        readonly commentsFilter: string = '',
        readonly markedComments: string[] = [],
        readonly featureOrTypeIdForComments: string | number | null = null,
        readonly roadSegments: RoadSegment[] = [],
        readonly selectedRoadSegment: RoadSegment = null,
        readonly loadingAttributes: Record<string, Record<number, boolean>> = {},
        readonly loadingSpecialAttributes: Record<string, Record<SpecialAttribute, boolean>> = {},
        readonly createAbstractObjectVisible: boolean = false,
        readonly createObjectModalVisible: boolean = false,
        readonly createObjectTypeId: number | null = null,
        readonly createObjectWkt: string | null = null,
        readonly referenceError: boolean = false,
        readonly creatingObjectGeometry: GeometryType | null = null,
        readonly featuresPage: number = 0,
        readonly featuresPageSize: number = localStorage.getItem('pageSize') ? Number.parseInt(localStorage.getItem('pageSize'), 10) : 100,
        readonly featureSearchString: string = '',
    ) { }

    private create(params: Partial<FeaturesState>) {
        const state = new FeaturesState(
            params.contractId,
            params.isFetching,
            params.featureTypes,
            params.selectedFeatureTypeId,
            params.selectedFeatureType,
            params.additionalSelectedFeatureTypes,
            params.features,
            params.selectedFeatures,
            params.user,
            params.isShowingExistingFeatures,
            params.existingFeatures,
            params.closings,
            params.showingFullscreen,
            params.splitScreenDirection,
            params.editingGeometryFeatureId,
            params.sceneData,
            params.ground3dData,
            params.datacatalog,
            params.gridFocus,
            params.showEditor,
            params.sortSetting,
            params.showSubmittedFeatures,
            params.preserveFocus,
            params.showOnlyObjektlistAttributes,
            params.showOnlySelectedFeatures,
            params.showQualityParams,
            params.useDescriptiveName,
            params.popup,
            params.selectedNvdbRoadObject,
            params.missingAttributeTypes,
            params.fetchingMissingAttributeTypes,
            params.fetchingSensitiveAttributeTypes,
            params.sensitiveAttributesInURL,
            params.copyGeometryModalVisible,
            params.copiableGeometries,
            params.fetchingCopiableGeometries,
            params.copyingGeometriesBetweenFeatures,
            params.roadSystemReferences,
            params.comments,
            params.commentsModalVisible,
            params.commentsFilter,
            params.markedComments,
            params.featureOrTypeIdForComments,
            params.roadSegments,
            params.selectedRoadSegment,
            params.loadingAttributes,
            params.loadingSpecialAttributes,
            params.createAbstractObjectVisible,
            params.createObjectModalVisible,
            params.createObjectTypeId,
            params.createObjectWkt,
            params.referenceError,
            params.creatingObjectGeometry,
            params.featuresPage,
            params.featuresPageSize,
            params.featureSearchString,
        );
        return state;
    }

    update = ({
        contractId = this.contractId,
        isFetching = this.isFetching,
        featureTypes = this.featureTypes,
        selectedFeatureTypeId = this.selectedFeatureTypeId,
        selectedFeatureType = this.selectedFeatureType,
        additionalSelectedFeatureTypes = this.additionalSelectedFeatureTypes,
        features = this.features,
        selectedFeatures = this.selectedFeatures,
        user = this.user,
        isShowingExistingFeatures = this.isShowingExistingFeatures,
        existingFeatures = this.existingFeatures,
        closings = this.closings,
        showingFullscreen = this.showingFullscreen,
        splitScreenDirection = this.splitScreenDirection,
        editingGeometryFeatureId = this.editingGeometryFeatureId,
        sceneData = this.sceneData,
        ground3dData = this.ground3dData,
        datacatalog = this.datacatalog,
        gridFocus = this.gridFocus,
        showEditor = this.showEditor,
        sortSetting = this.sortSetting,
        showSubmittedFeatures = this.showSubmittedFeatures,
        preserveFocus = this.preserveFocus,
        showOnlyObjektlistAttributes = this.showOnlyObjektlistAttributes,
        showOnlySelectedFeatures = this.showOnlySelectedFeatures,
        showQualityParams = this.showQualityParams,
        useDescriptiveName = this.useDescriptiveName,
        popup = this.popup,
        selectedNvdbRoadObject = this.selectedNvdbRoadObject,
        missingAttributeTypes = this.missingAttributeTypes,
        fetchingMissingAttributeTypes = this.fetchingMissingAttributeTypes,
        fetchingSensitiveAttributeTypes = this.fetchingSensitiveAttributeTypes,
        hasSensitiveAttributesInURL = this.sensitiveAttributesInURL,
        copyGeometryModalVisible = this.copyGeometryModalVisible,
        copiableGeometries = this.copiableGeometries,
        fetchingCopiableGeometries = this.fetchingCopiableGeometries,
        copyingGeometriesBetweenFeatures = this.copyingGeometriesBetweenFeatures,
        roadSystemReferences = this.roadSystemReferences,
        comments = this.comments,
        commentsModalVisible = this.commentsModalVisible,
        commentsFilter = this.commentsFilter,
        markedComments = this.markedComments,
        featureOrTypeIdForComments = this.featureOrTypeIdForComments,
        roadSegments = this.roadSegments,
        selectedRoadSegment = this.selectedRoadSegment,
        loadingAttributes = this.loadingAttributes,
        loadingSpecialAttributes = this.loadingSpecialAttributes,
        createAbstractObjectVisible = this.createAbstractObjectVisible,
        createObjectModalVisible = this.createObjectModalVisible,
        createObjectTypeId = this.createObjectTypeId,
        createObjectWkt = this.createObjectWkt,
        referenceError = this.referenceError,
        creatingObjectGeometry = this.creatingObjectGeometry,
        featuresPage = this.featuresPage,
        featuresPageSize = this.featuresPageSize,
        featureSearchString = this.featureSearchString,
    } = {}) => ({
        contractId,
        isFetching,
        featureTypes,
        selectedFeatureTypeId,
        selectedFeatureType,
        additionalSelectedFeatureTypes,
        features,
        selectedFeatures,
        user,
        isShowingExistingFeatures,
        existingFeatures,
        closings,
        showingFullscreen,
        splitScreenDirection,
        editingGeometryFeatureId,
        sceneData,
        ground3dData,
        datacatalog,
        gridFocus,
        showEditor,
        sortSetting,
        showSubmittedFeatures,
        preserveFocus,
        showOnlyObjektlistAttributes,
        showOnlySelectedFeatures,
        showQualityParams,
        useDescriptiveName,
        popup,
        selectedNvdbRoadObject,
        missingAttributeTypes,
        fetchingMissingAttributeTypes,
        fetchingSensitiveAttributeTypes,
        hasSensitiveAttributesInURL,
        copyGeometryModalVisible,
        copiableGeometries,
        fetchingCopiableGeometries,
        copyingGeometriesBetweenFeatures,
        roadSystemReferences,
        comments,
        commentsModalVisible,
        commentsFilter,
        markedComments,
        featureOrTypeIdForComments,
        roadSegments,
        selectedRoadSegment,
        loadingAttributes,
        loadingSpecialAttributes,
        createAbstractObjectVisible,
        createObjectModalVisible,
        createObjectTypeId,
        createObjectWkt,
        referenceError,
        creatingObjectGeometry,
        featuresPage,
        featuresPageSize,
        featureSearchString,
    })

    public withSelectedNvdbRoadObject(selectedNvdbRoadObject: NvdbFeature): FeaturesState {
        return this.create(this.update({selectedNvdbRoadObject}));
    }

    public withShowQualityParams(showQualityParams: boolean): FeaturesState {
        return this.create(this.update({showQualityParams}));
    }

    public withShowOnlyObjektlistAttributes(showOnlyObjektlistAttributes: boolean): FeaturesState {
        return this.create(this.update({showOnlyObjektlistAttributes}));
    }

    public withPreserveFocus(preserveFocus: PreserveFocusData): FeaturesState {
        return this.create(this.update({preserveFocus}));
    }

    public withContractId(contractId: string): FeaturesState {
        return this.create(this.update({contractId}));
    }

    public withIsFetching(isFetching: boolean): FeaturesState {
        return this.create(this.update({isFetching}));
    }

    public withFeatureTypes(featureTypes: FeatureTypeSummary[]): FeaturesState {
        return this.create(this.update({featureTypes}));
    }

    public withSelectedFeatureTypeId(selectedFeatureTypeId: number): FeaturesState {
        return this.create(this.update({selectedFeatureTypeId}));
    }

    public withEditingGeometry(featureId: string): FeaturesState {
        const editingGeometryFeatureId = featureId;
        return this.create(this.update({editingGeometryFeatureId}));
    }

    public withNoSelectedFeatureType(): FeaturesState {
        const selectedFeatureTypeId: number | null = null;
        const selectedFeatureType: DisplayFeatureType | null = null;
        const additionalSelectedFeatureTypes: DisplayFeatureType[] = [];
        const selectedFeatures: string[] | null = null;
        const isShowingExistingFeatures: boolean | null = null;
        const existingFeatures: NvdbFeature[] | null = null;

        return this.create(this.update({
            selectedFeatureTypeId,
            selectedFeatureType,
            additionalSelectedFeatureTypes,
            selectedFeatures,
            isShowingExistingFeatures,
            existingFeatures
        }));
    }

    public withSelectedFeatureType(selectedFeatureType: DisplayFeatureType): FeaturesState {
        return this.create(this.update({selectedFeatureType}));
    }

    public withAdditionalSelectedFeatureType(additionalSelectedFeatureTypes: DisplayFeatureType[]): FeaturesState {
        return this.create(this.update({additionalSelectedFeatureTypes}));
    }

    public withSelectedFeatures(selectedFeatures: string[]): FeaturesState {
        return this.create(this.update({selectedFeatures}));
    }

    public withFeatures(features: DisplayFeature[]): FeaturesState {
        return this.create(this.update({features}));
    }

    public withUser(user: UserSession): FeaturesState {
        return this.create(this.update({user}));
    }

    public withIsShowingExistingFeatures(isShowingExistingFeatures: boolean): FeaturesState {
        return this.create(this.update({isShowingExistingFeatures}));
    }

    public withExistingFeatures(existingFeatures: NvdbFeature[]): FeaturesState {
        return this.create(this.update({existingFeatures}));
    }

    public withClosings(closings: DisplayFeature[]): FeaturesState {
        return this.create(this.update({closings}));
    }

    public toggleFullscreen(): FeaturesState {
        const showingFullscreen = !this.showingFullscreen;
        return this.create(this.update({showingFullscreen}));
    }

    public toggleSplitScreen(): FeaturesState {
        const splitScreenDirection: splitDirection = (this.splitScreenDirection == 'vertical') ? 'horizontal' : 'vertical';
        return this.create(this.update({splitScreenDirection}));
    }

    public withSceneData(sceneData: SceneData): FeaturesState {
        return this.create(this.update({sceneData}));
    }

    public withGround3dData(ground3dData: Ground3dData): FeaturesState {
        return this.create(this.update({ground3dData}));
    }
    public withGridFocus(gridFocus: GridCoordinates): FeaturesState {
        return this.create(this.update({gridFocus}));
    }

    public withDatacatalog(datacatalog: datacatalog): FeaturesState {
        return this.create(this.update({datacatalog}));
    }

    public withShowEditor(showEditor: boolean): FeaturesState {
        return this.create(this.update({showEditor}));
    }

    public withSortSetting(sortSetting: SortSetting): FeaturesState {
        return this.create(this.update({sortSetting}));
    }

    public withShowSubmittedFeatures(showSubmittedFeatures: boolean): FeaturesState {
        return this.create(this.update({showSubmittedFeatures}));
    }

    public withShowOnlySelectedFeatures(showOnlySelectedFeatures: boolean): FeaturesState {
        return this.create(this.update({showOnlySelectedFeatures}));
    }

    public withUseDescriptiveName(useDescriptiveName: boolean): FeaturesState {
        return this.create(this.update({useDescriptiveName}));
    }

    public withPopup(popup: PopupType): FeaturesState {
        return this.create(this.update({popup}));
    }

    public withMissingAttributeTypes(missingAttributeTypes: AttributeType[]): FeaturesState {
        return this.create(this.update({missingAttributeTypes}));
    }

    public withFetchingMissingAttributeTypes(fetchingMissingAttributeTypes: boolean): FeaturesState {
        return this.create(this.update({fetchingMissingAttributeTypes}));
    }

    public withCopyGeometryModalVisible(isVisible: boolean): FeaturesState {
        return this.create(this.update({copyGeometryModalVisible: isVisible}));
    }

    public withFetchingSensitiveAttributeTypes(fetchingSensitiveAttributeTypes: boolean): FeaturesState {
        return this.create(this.update({fetchingSensitiveAttributeTypes}));
    }

    public withSensitiveAttributesInURL(sensitive: boolean): FeaturesState {
        return this.create(this.update({hasSensitiveAttributesInURL: sensitive}));
    }

    public withCopiableGeometries(features: CopiableGeometryFeature[]): FeaturesState {
        return this.create(
            this.update({copiableGeometries: features})
        );
    }

    public withFetchingCopiableGeometries(fetching: boolean): FeaturesState {
        return this.create(this.update({fetchingCopiableGeometries: fetching}));
    }

    public withCopyingGeometriesBetweenFeatures(copying: boolean): FeaturesState {
        return this.create(this.update({copyingGeometriesBetweenFeatures: copying}));
    }

    public withRoadSystemReferences(refs: Record<string, string>): FeaturesState {
        return this.create(this.update({roadSystemReferences: refs}));
    }

    public withComments(comments: OrderedComments): FeaturesState {
        return this.create(this.update({comments}));
    }

    public withCommentsModalVisible(visible: boolean): FeaturesState {
        return this.create(this.update({commentsModalVisible: visible}));
    }

    public withCommentsFilter(filter: string): FeaturesState {
        return this.create(this.update({commentsFilter: filter}));
    }

    public withMarkedComments(markedComments: string[]): FeaturesState {
        return this.create(this.update({markedComments}));
    }

    public withFeatureOrTypeIdForComments(id: string | number | null): FeaturesState {
        return this.create(this.update({featureOrTypeIdForComments: id}));
    }

    public withRoadSegments(segments: RoadSegment[]) {
        return this.create(this.update({roadSegments: segments}));
    }

    public withSelectedRoadSegment(segment: RoadSegment) {
        return this.create(this.update({selectedRoadSegment: segment}));
    }

    public withCreateObjectVisible(visible: boolean) {
        return this.create(this.update({createObjectModalVisible: visible}));
    }

    public withCreateObjectTypeId(id: number) {
        return this.create(this.update({createObjectTypeId: id}));
    }

    public withCreateObjectWkt(wkt: string) {
        return this.create(this.update({createObjectWkt: wkt}));
    }

    public withSetReferenceError(error: boolean) {
        return this.create(this.update({referenceError: error}));
    }

    public withCreatingObjectGeometry(creating: GeometryType) {
        return this.create(this.update({creatingObjectGeometry: creating}));
    }

    public withLoadingAttributes(loadingAttributes: Record<string, Record<number, boolean>>): FeaturesState {
        return this.create(this.update({loadingAttributes}));
    }

    public withLoadingSpecialAttributes(loadingSpecialAttributes: Record<string, Record<SpecialAttribute, boolean>>): FeaturesState {
        return this.create(this.update({loadingSpecialAttributes}));
    }

    public withFeaturesPage(page: number) {
        return this.create(this.update({featuresPage: page}));
    }
    public withFeaturesPageSize(pageSize: number) {
        return this.create(this.update({featuresPageSize: pageSize}));
    }

    public withFeatureSearchString(featureSearchString: string): FeaturesState {
        return this.create(this.update({featureSearchString}));
    }
}
